import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { EmptyState, LoadingSpinner } from "/imports/ui/components/shared";
import { PackHorizonBanner } from "/imports/ui/components/booking";
import ClassFilterSidebar from "./components/ClassFilterSidebar";
import ClassDayStrip from "./components/ClassDayStrip";
import ClassDaySections from "./components/ClassDaySections";
import ClassWeekGrid from "./components/ClassWeekGrid";
import ClassOptionsPopover from "./components/ClassOptionsPopover";

// Desktop layout — sticky filter sidebar + a results pane that toggles between
// Day (scroll through the week, grouped by day) and Week (a 7-day grid with one
// column per day). Both are week-scoped.
function ClassesDesktopLayout({ cls, scrollSpy }) {
  const { navigate, slug, viewMode, setViewMode, weekLabel, canGoPrevWeek, goPrevWeek, goNextWeek, loading, error, weekHasClasses, emptyStateProps } =
    cls;
  const { stickyRef } = scrollSpy;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
        <IconButton size="small" onClick={cls.goBack} aria-label="Go back" sx={{ ml: -0.5 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ fontSize: "1.75rem", fontWeight: 800, letterSpacing: "-0.02em" }}>Group Classes</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: cls.ownedPackHorizon ? 1.5 : 3 }}>
        Browse the week and join sessions with our trainers.
      </Typography>
      <PackHorizonBanner
        horizon={cls.ownedPackHorizon}
        start={cls.ownedPackStart}
        count={cls.ownedPackCount}
        capped={cls.ownedPackCapped}
        onJump={cls.goToPackStart}
        sx={{ mb: 3, maxWidth: "42rem" }}
      />

      <Box sx={{ display: "flex", gap: 4, alignItems: "flex-start" }}>
        <ClassFilterSidebar cls={cls} />

        {/* Results pane */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Sticky header: view toggle + week nav + (day-strip in Day mode). */}
          <Box ref={stickyRef} sx={{ position: "sticky", top: 64, zIndex: 4, bgcolor: "background.default", pt: 0.5, pb: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: viewMode === "day" ? 1.5 : 0,
                gap: 2,
                flexWrap: "wrap"
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  p: 0.4,
                  borderRadius: "9999px",
                  border: "1.5px solid",
                  borderColor: "divider",
                  bgcolor: "#fff",
                  gap: 0.4
                }}
              >
                {["day", "week"].map((m) => {
                  const active = viewMode === m;
                  return (
                    <ButtonBase
                      key={m}
                      onClick={() => setViewMode(m)}
                      sx={{ px: 2, py: 0.55, borderRadius: "9999px", bgcolor: active ? "primary.main" : "transparent", transition: "all 0.15s ease" }}
                    >
                      <Typography
                        sx={{ fontSize: "0.8rem", fontWeight: 600, textTransform: "capitalize", color: active ? "#fff" : "text.secondary" }}
                      >
                        {m}
                      </Typography>
                    </ButtonBase>
                  );
                })}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography sx={{ fontSize: "1rem", fontWeight: 700, letterSpacing: "-0.01em" }}>{weekLabel}</Typography>
                {viewMode === "week" && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={goPrevWeek}
                      disabled={!canGoPrevWeek}
                      sx={{ border: "1px solid", borderColor: "divider", borderRadius: "50%", width: "2.125rem", height: "2.125rem" }}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={goNextWeek}
                      sx={{ border: "1px solid", borderColor: "divider", borderRadius: "50%", width: "2.125rem", height: "2.125rem" }}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>

            {viewMode === "day" && <ClassDayStrip cls={cls} scrollSpy={scrollSpy} />}
          </Box>

          {loading ? (
            <LoadingSpinner message="Loading classes..." />
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : !weekHasClasses ? (
            <EmptyState {...emptyStateProps} />
          ) : viewMode === "day" ? (
            <ClassDaySections cls={cls} scrollSpy={scrollSpy} />
          ) : (
            <ClassWeekGrid cls={cls} />
          )}
        </Box>
      </Box>

      <ClassOptionsPopover cls={cls} />
    </Box>
  );
}

export default ClassesDesktopLayout;
